import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { catchError, forkJoin, map, of } from "rxjs";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { DatePickerModule } from "primeng/datepicker";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { TooltipModule } from "primeng/tooltip";
import { PopoverModule } from "primeng/popover";
import { ChartModule } from "primeng/chart";
import {
  UpdateSampleLineNicknameRequestDto,
  UpdateSampleLineShipDateRequestDto,
  WorkflowSampleLineDto,
  WorkflowSampleLineItemDto,
} from "../../../../core/models/project-workflow/workflowSampleLineDto";
import { AddWorkflowLineTrackingDto } from "../../../../core/models/project-workflow/addWorkflowLineTrackingDto";
import { AddProjectWorkflowLineNotesDto } from "../../../../core/models/project-workflow/addProjectWorkflowLineNotesDto";
import { AddWorkflowTrackingRequestDto } from "../../../../core/models/project-workflow/addWorkflowTrackingRequestDto";
import { ClientSideLineTrackingListDto } from "../../../../core/models/project-workflow/clientSideLineTrackingListDto";
import { DeleteWorkflowLineTrackingDto } from "../../../../core/models/project-workflow/deleteWorkflowLineTracking";
import {
  DimensionDto,
  WeightDto,
} from "../../../../core/models/project-workflow/dimensionDataDtos";
import { ProductionWorkflowLineTrackingDto } from "../../../../core/models/project-workflow/productionWorkflowLineTrackingDto";
import { ProjectWorkflowTrackingNumberDetailsDto } from "../../../../core/models/project-workflow/GetProjectWorkflowTrackingDetailsDto";
import { TrackingDetailsBindingDto } from "../../../../core/models/project-workflow/trackingDetailsBindingDto";
import { TrackingHistoryListDto } from "../../../../core/models/project-workflow/trackingHistoryListDto";
import { UpdateManualTrackingStatusDto } from "../../../../core/models/project-workflow/updateManualTrackingStatusRequestDto";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { LoaderService } from "../../../../core/services/loader.service";
import { ProjectWorkflowService } from "../../../../core/services/project-workflow.service";
import { SharedService } from "../../../../core/services/shared.service";
import { SpinnerComponent } from "../../../../shared/components/spinner/spinner.component";
import { AddTrackingComponent } from "../../add-tracking/add-tracking.component";
import { TrackingDetailPopupComponent } from "../../tracking-detail-popup/tracking-detail-popup.component";
import { TrackingListComponent } from "../../tracking-list/tracking-list.component";
import { TrackingListPopupComponent } from "../../tracking-list-popup/tracking-list-popup.component";
import { WorkflowLineNotesComponent } from "../../workflow-line-notes/workflow-line-notes.component";
import { AddSamplePoModalComponent } from "./add-sample-po-modal/add-sample-po-modal.component";

@Component({
  selector: "jni-samples-section",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    PopoverModule,
    ChartModule,
    SpinnerComponent,
    AddTrackingComponent,
    TrackingListComponent,
    TrackingListPopupComponent,
    TrackingDetailPopupComponent,
    WorkflowLineNotesComponent,
    AddSamplePoModalComponent,
  ],
  templateUrl: "./samples-section.component.html",
  styleUrls: ["./samples-section.component.scss"],
  providers: [DatePipe],
})
export class SamplesSectionComponent {
  private readonly _workflowService = inject(ProjectWorkflowService);
  private readonly _toastr = inject(JniToastrService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _sharedService = inject(SharedService);
  private readonly _datePipe = inject(DatePipe);

  workflowId = input<number>(0);
  /** Client name of the workflow — used to filter sample POs in the Add Line modal. */
  clientName = input<string>("");
  addLineLoaderEvent = output<boolean>();

  samples = signal<WorkflowSampleLineDto[]>([]);
  loading = signal(false);
  saving = signal(false);

  selectedSamples: WorkflowSampleLineDto[] = [];
  isCollapsed = false;
  showAddSamplePoModal = false;

  /** The id of the row currently being edited for nickname, or null when no row is in edit mode. */
  editingRowId = signal<number | null>(null);
  /** The draft nickname value while editing. */
  editingNickname = signal("");
  /** True while the nickname save request is in flight. */
  nicknameSaving = signal(false);

  // ─── Tracking state ────────────────────────────────────────
  workflowTrackingList: ProductionWorkflowLineTrackingDto[] = [];
  trackingDetailsList: ClientSideLineTrackingListDto[] = [];
  trackingPopupAddMode = false;
  /**
   * Modal visibility / loading flags below are signals rather than plain booleans.
   * They are set from async HTTP `subscribe` callbacks, and this component uses
   * `OnPush` change detection — a plain boolean mutated outside a template-originated
   * event does not mark the view dirty, so the dialog would silently stay closed until
   * a later unrelated event (e.g. a second click) happened to trigger change detection.
   * Signals notify their consumers directly, so the dialog opens on the first click.
   */
  addTrackingModal = signal(false);
  trackingListModal = signal(false);
  trackingHistoryModalOpen = signal(false);
  trackingDetailsModalOpen = signal(false);
  smallTrackingListLoader = signal(false);
  disableAddTracking = signal(false);

  /** Tracking context for the sample line currently being tracked. */
  trackingLineVendorSKU = "";
  trackingLineQuantity = 0;
  trackingLineRecIdD365 = 0;
  trackingLineShippingMethod = "";
  trackingLineShippingMethodName = "";
  addTrackingLineNumber = 0;
  addTrackingPurchaseOrder = "";

  trackingHistoryList: TrackingHistoryListDto[] = [];
  historyTrackingNumber = "";
  trackingNumberDetail = {} as ProjectWorkflowTrackingNumberDetailsDto;
  trackingWeight: WeightDto[] = [];
  trackingDimension: DimensionDto[] = [];
  isUPS = false;
  trackingDetailsBindingObj = {} as TrackingDetailsBindingDto;

  manualShippingMethods = [] as { code: string; shippingMethod: string }[];
  trackings = "";
  manualMethod = "";
  displayAddManualTrackingModal = signal(false);

  // ─── Notes state ───────────────────────────────────────────────────
  displaySidebarForNotes = false;
  notes = "";
  noteLineRecId = 0;

  fedExStatusCategories: Record<string, string[]> = {
    Shipped: [
      "DO",
      "LC",
      "DS",
      "PD",
      "CA",
      "US",
      "RD",
      "IP",
      "HP",
      "RG",
      "RP",
      "RS",
    ],
    "In Transit": [
      "AR",
      "AF",
      "AC",
      "IT",
      "OX",
      "PM",
      "DP",
      "DR",
      "CP",
      "OD",
      "OF",
    ],
    Delivered: ["DL"],
    Exception: ["DE", "SE", "CD", "DY", "HL"],
  };

  constructor() {
    effect(() => {
      const workflowId = this.workflowId();
      if (workflowId > 0) {
        this.loadSamples(workflowId);
      }
    });
  }

  private buildShippingStatusVisualization(
    sample: WorkflowSampleLineDto,
    counts?: {
      deliveredCount: number;
      inTransitCount: number;
      shippedCount: number;
      exceptionCount: number;
    }
  ): WorkflowSampleLineDto {
    const deliveredCount = counts?.deliveredCount ?? sample.deliveredCount ?? 0;
    const inTransitCount = counts?.inTransitCount ?? sample.inTransitCount ?? 0;
    const shippedCount = counts?.shippedCount ?? sample.shippedCount ?? 0;
    const exceptionCount = counts?.exceptionCount ?? sample.exceptionCount ?? 0;
    const trackingCount =
      sample.trackingCount ??
      deliveredCount + inTransitCount + shippedCount + exceptionCount;

    return {
      ...sample,
      deliveredCount,
      inTransitCount,
      shippedCount,
      exceptionCount,
      stackedDataOptions: {
        labels: ["Status"],
        datasets: [
          {
            label: "Delivered",
            data: [deliveredCount],
            backgroundColor: "#669b57",
          },
          {
            label: "InTransit",
            data: [inTransitCount],
            backgroundColor: "#00a8f3",
          },
          {
            label: "Shipped",
            data: [shippedCount],
            backgroundColor: "#ffcb00",
          },
          {
            label: "Exception",
            data: [exceptionCount],
            backgroundColor: "#e93e3e",
          },
        ],
      },
      chartOptions: {
        indexAxis: "y",
        plugins: {
          legend: {
            display: false,
          },
        },
        responsive: true,
        scales: {
          x: {
            stacked: true,
            display: false,
            max: trackingCount,
          },
          y: {
            stacked: true,
            display: false,
          },
        },
        datasets: {
          bar: {
            stack: true,
          },
        },
      },
    };
  }

  private getCountsFromTrackingList(
    trackingRows: ProductionWorkflowLineTrackingDto[]
  ): {
    deliveredCount: number;
    inTransitCount: number;
    shippedCount: number;
    exceptionCount: number;
  } {
    let deliveredCount = 0;
    let inTransitCount = 0;
    let shippedCount = 0;
    let exceptionCount = 0;

    trackingRows.forEach(row => {
      const status = (row.deliveryStatus ?? "").trim().toLowerCase();
      if (status.includes("delivered")) {
        deliveredCount++;
        return;
      }

      if (
        status.includes("in-transit") ||
        status.includes("in transit") ||
        status.includes("transit")
      ) {
        inTransitCount++;
        return;
      }

      if (status.includes("exception") || status.includes("delay")) {
        exceptionCount++;
        return;
      }

      if (status.includes("shipped")) {
        shippedCount++;
      }
    });

    return {
      deliveredCount,
      inTransitCount,
      shippedCount,
      exceptionCount,
    };
  }

  private enrichSampleShippingStatuses(
    workflowId: number,
    sampleRows: WorkflowSampleLineDto[]
  ): void {
    const requests = sampleRows.map(sample => {
      if (!sample.lineRecIdD365 || (sample.trackingCount ?? 0) <= 0) {
        return of({
          sampleId: sample.id,
          counts: {
            deliveredCount: 0,
            inTransitCount: 0,
            shippedCount: 0,
            exceptionCount: 0,
          },
        });
      }

      return this._workflowService
        .getSampleLineTrackingList(workflowId, sample.lineRecIdD365)
        .pipe(
          map(res => ({
            sampleId: sample.id,
            counts: this.getCountsFromTrackingList(res.data ?? []),
          })),
          catchError(() =>
            of({
              sampleId: sample.id,
              counts: {
                deliveredCount: 0,
                inTransitCount: 0,
                shippedCount: 0,
                exceptionCount: 0,
              },
            })
          )
        );
    });

    forkJoin(requests).subscribe(results => {
      const countBySampleId = new Map(
        results.map(result => [result.sampleId, result.counts])
      );

      this.samples.update(rows =>
        rows.map(row =>
          this.buildShippingStatusVisualization(
            row,
            countBySampleId.get(row.id)
          )
        )
      );
    });
  }

  /** Loads the sample PO lines linked to the workflow. */
  private loadSamples(workflowId: number): void {
    this.loading.set(true);
    this._workflowService.getWorkflowSampleLines(workflowId).subscribe({
      next: res => {
        const mappedSamples = (res.data ?? []).map(sample =>
          this.buildShippingStatusVisualization(sample)
        );

        this.samples.set(mappedSamples);
        this.enrichSampleShippingStatuses(workflowId, mappedSamples);
        this.selectedSamples = [];
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  onToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  onAddLine(): void {
    this.showAddSamplePoModal = true;
  }

  /** Enters edit mode for the nickname of the given row. */
  startEditNickname(sample: WorkflowSampleLineDto): void {
    this.editingRowId.set(sample.id);
    this.editingNickname.set(sample.nickName ?? "");
  }

  /** Exits edit mode without saving. */
  cancelEditNickname(): void {
    this.editingRowId.set(null);
    this.editingNickname.set("");
  }

  /** Saves the draft nickname to the backend and updates the local list on success. */
  saveNickname(sample: WorkflowSampleLineDto): void {
    if (this.nicknameSaving() || this.workflowId() <= 0) {
      return;
    }
    const nickName = this.editingNickname().trim();
    const request: UpdateSampleLineNicknameRequestDto = {
      id: sample.id,
      workflowId: this.workflowId(),
      nickName,
    };
    this.nicknameSaving.set(true);
    this._workflowService.updateWorkflowSampleLineNickname(request).subscribe({
      next: res => {
        this.nicknameSaving.set(false);
        if (res.status?.toLowerCase() === "success") {
          this.samples.update(list =>
            list.map(s => (s.id === sample.id ? { ...s, nickName } : s))
          );
          this.editingRowId.set(null);
          this.editingNickname.set("");
          this._toastr.success("Nickname updated successfully");
        } else {
          this._toastr.error(res.message || "Failed to update nickname");
        }
      },
      error: () => {
        this.nicknameSaving.set(false);
        this._toastr.error("Failed to update nickname");
      },
    });
  }

  /** Saves the selected ESD date for a sample line to the backend. */
  onSampleShipDateSelection(event: Date, sample: WorkflowSampleLineDto): void {
    const shipDate = this._datePipe.transform(event, "MM-dd-yyyy");
    const request: UpdateSampleLineShipDateRequestDto = {
      id: sample.id,
      workflowId: this.workflowId(),
      shipDate,
    };
    this._workflowService.updateWorkflowSampleLineShipDate(request).subscribe({
      next: res => {
        if (res.status?.toLowerCase() === "success") {
          this.samples.update(list =>
            list.map(s =>
              s.id === sample.id ? { ...s, shipDate: event.toISOString() } : s
            )
          );
          this._toastr.success("ESD updated successfully");
        } else {
          this._toastr.error(res.message || "Failed to update ESD");
        }
      },
      error: () => this._toastr.error("Failed to update ESD"),
    });
  }

  /** Persists the sample PO lines selected in the modal against the workflow. */
  onLinesSelected(lines: WorkflowSampleLineItemDto[]): void {
    if (lines.length === 0 || this.workflowId() <= 0) {
      return;
    }
    this.saving.set(true);
    this._loaderService.setLoaderState(true);
    this._workflowService
      .addWorkflowSampleLines({ workflowId: this.workflowId(), lines })
      .subscribe({
        next: res => {
          this.samples.set(res.data ?? []);
          this.selectedSamples = [];
          this.saving.set(false);
          this.showAddSamplePoModal = false;
          this._loaderService.setLoaderState(false);
          this._toastr.success("Sample PO lines added successfully");
        },
        error: () => {
          this.saving.set(false);
          this._loaderService.setLoaderState(false);
          this._toastr.error("Failed to add sample PO lines");
        },
      });
  }

  onDelete(): void {
    if (this.selectedSamples.length === 0) {
      this._toastr.warning("Select at least one sample line to delete");
      return;
    }
    this._loaderService.setLoaderState(true);
    this._workflowService
      .deleteWorkflowSampleLines({
        workflowId: this.workflowId(),
        ids: this.selectedSamples.map(x => x.id),
      })
      .subscribe({
        next: () => {
          this._loaderService.setLoaderState(false);
          this._toastr.success("Sample PO lines removed successfully");
          this.loadSamples(this.workflowId());
        },
        error: () => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Failed to remove sample PO lines");
        },
      });
  }

  // ─── Tracking ──────────────────────────────────────────────────────

  /** Opens the Add Tracking Information modal for the selected sample line. */
  onAddTracking(): void {
    if (this.selectedSamples.length === 0) {
      this._toastr.error("Error", "Select a Sample to Add Tracking Numbers");
      return;
    }
    if (this.selectedSamples.length > 1) {
      this._toastr.error(
        "Error",
        "Select only one Sample to Add Tracking Numbers"
      );
      return;
    }
    this.setTrackingContext(this.selectedSamples[0]);
    this.trackingPopupAddMode = true;
    this.getSampleTrackingList();
  }

  /** Opens the Tracking Information list modal when the tracking count is clicked. */
  showTrackingDetailList(sample: WorkflowSampleLineDto): void {
    this.setTrackingContext(sample);
    this.trackingPopupAddMode = false;
    this.getSampleTrackingList();
  }

  /** Re-fetches the tracking list for the sample line currently open in the modal. */
  refreshTrackingList(): void {
    this.trackingPopupAddMode = false;
    this.getSampleTrackingList();
  }

  private setTrackingContext(sample: WorkflowSampleLineDto): void {
    this.trackingLineVendorSKU = sample.vendorSku ?? "";
    this.trackingLineQuantity = sample.quantity ?? 0;
    this.trackingLineRecIdD365 = sample.lineRecIdD365 ?? 0;
    this.trackingLineShippingMethod = sample.method ?? "";
    this.trackingLineShippingMethodName = sample.shippingMethodName ?? "";
    this.addTrackingPurchaseOrder = sample.samplePONumber ?? "";
    this.addTrackingLineNumber = 0;
  }

  /** Fetches the tracking rows for the current sample line and opens the relevant modal. */
  private getSampleTrackingList(): void {
    this._loaderService.setLoaderState(true);
    this.smallTrackingListLoader.set(true);
    this._workflowService
      .getSampleLineTrackingList(this.workflowId(), this.trackingLineRecIdD365)
      .subscribe({
        next: res => {
          this.workflowTrackingList = res?.data ?? [];
          const delivered = this.workflowTrackingList.find(
            item => (item.deliveryStatus ?? "").toLowerCase() === "delivered"
          );
          this.disableAddTracking.set(!!delivered);

          if (this.trackingPopupAddMode) {
            this.addTrackingModal.set(true);
            this.trackingListModal.set(false);
          } else {
            this.addTrackingModal.set(false);
            this.trackingListModal.set(true);
            this.trackingDetailsList = this.workflowTrackingList.map(obj => {
              const tracking = {} as ClientSideLineTrackingListDto;
              tracking.vendorSKU = obj.vendorSKU;
              tracking.quantity = obj.quantity;
              tracking.trackingNumber = obj.trackingNumber;
              tracking.deliveryStatus = obj.deliveryStatus;
              tracking.isFromDatabase = true;
              tracking.shippingMethod = obj.shippingMethod;
              tracking.id = obj.trackingLineId;
              tracking.lineRecIdD365 = this.trackingLineRecIdD365;
              tracking.shippingMethodName = obj.shippingMethodName;
              tracking.masterTrackingNumber = obj.masterTrackingNumber;
              tracking.isMasterTracking = obj.isMasterTracking;
              tracking.isManualTracking = obj.isManualTracking;
              tracking.deliveryStatusId = obj.isManualTracking
                ? (obj.deliveryStatusId ?? 0)
                : 0;
              return tracking;
            });
            this.trackingDetailsList = this._sharedService.sortTrackingData(
              this.trackingDetailsList
            );
          }
          this._loaderService.setLoaderState(false);
          this.smallTrackingListLoader.set(false);
        },
        error: res => {
          this._toastr.error("Error", res.message);
          this._loaderService.setLoaderState(false);
          this.smallTrackingListLoader.set(false);
        },
      });
  }

  /** Saves the tracking numbers entered in the Add Tracking / manual modals. */
  addTrackingData(
    event: {
      ShippingMethod: string;
      Quantity: number;
      VendorSKU: string;
      LineNumber: number;
      TrackingNumber: string;
      PurchaseOrder: string;
      IsMasterTracking: boolean;
    }[],
    isManualTracking = false
  ): void {
    this._loaderService.setLoaderState(true);
    if (event.length <= 0) {
      this._toastr.error("Error", "Add a Tracking Number to Save Data");
      this._loaderService.setLoaderState(false);
      return;
    }

    const payload: AddWorkflowLineTrackingDto[] = event.map(obj => ({
      vendorSKU: this.trackingLineVendorSKU ?? "",
      quantity: obj.Quantity ?? 0,
      trackingNumber: obj.TrackingNumber ?? "",
      lineRecIdD365: this.trackingLineRecIdD365 ?? 0,
      workflowId: this.workflowId() ?? 0,
      shippingMethodD365:
        obj.ShippingMethod && obj.ShippingMethod !== ""
          ? obj.ShippingMethod
          : (this.trackingLineShippingMethod ?? ""),
      salesOrderId: "",
      lineNumberD365: 0,
      isMasterTracking: obj.IsMasterTracking ?? false,
    }));

    const request: AddWorkflowTrackingRequestDto = {
      processWorkflowTrackingDetailsRequestList: payload,
      isManualTracking: isManualTracking,
    } as AddWorkflowTrackingRequestDto;

    this._workflowService.addSampleLineTracking(request).subscribe({
      next: res => {
        this._loaderService.setLoaderState(false);
        if (res && res.status === "Success") {
          this._toastr.success("Success", res.message);
        } else {
          this._toastr.error("Error", res.message);
        }
        this.loadSamples(this.workflowId());
        this.closeModalDialog();
      },
      error: res => {
        this._loaderService.setLoaderState(false);
        this._toastr.error("Error", res.message);
        this.closeModalDialog();
      },
    });
  }

  deleteTrackingRecord(event: ClientSideLineTrackingListDto): void {
    this._loaderService.setLoaderState(true);
    const payload = {} as DeleteWorkflowLineTrackingDto;
    payload.projectWorkflowTrackingLineId = event.id;
    this._workflowService.deleteWorkflowLineTracking(payload).subscribe({
      next: res => {
        if (res?.data) {
          this._toastr.success("Success", res.message);
          this.loadSamples(this.workflowId());
        } else {
          this._toastr.error("Error", "Failed to Delete Record");
        }
        this._loaderService.setLoaderState(false);
      },
      error: res => {
        this._toastr.error("Error", res.message);
        this._loaderService.setLoaderState(false);
      },
    });
  }

  showTrackingHistory(event: ClientSideLineTrackingListDto): void {
    this._workflowService
      .getTrackingHistoryByTrackingNumber(
        event.trackingNumber,
        this.trackingLineRecIdD365
      )
      .subscribe({
        next: res => {
          if (res?.data) {
            this.trackingHistoryList = res.data?.map(obj => {
              const mappedData = {} as TrackingHistoryListDto;
              mappedData.createdDate = obj.eventDate ?? "";
              mappedData.message =
                (obj.eventDescription ? obj.eventDescription + ", " : "") +
                (obj.address ? obj.address + ", " : "") +
                (obj.locationType ?? "");
              if (
                event.shippingMethod.includes("FX") &&
                obj.derivedStatusCode === obj.eventCode
              ) {
                mappedData.status =
                  Object.keys(this.fedExStatusCategories).find(category =>
                    this.fedExStatusCategories[category].includes(
                      obj.derivedStatusCode ?? ""
                    )
                  ) ??
                  obj.derivedStatus ??
                  "";
              } else {
                mappedData.status = obj.derivedStatus ?? "";
              }
              return mappedData;
            });
            this.historyTrackingNumber = event.trackingNumber;
            this.trackingHistoryModalOpen.set(true);
          } else {
            this._toastr.error("Error", "Unable to get Tracking History");
          }
        },
        error: () => {
          this._toastr.error("Error", "Unable to get Tracking History");
        },
      });
  }

  showTrackingDetails(event: ClientSideLineTrackingListDto): void {
    this.getTrackingDetails(event.trackingNumber);
  }

  private getTrackingDetails(trackingNumber: string): void {
    if (!trackingNumber) {
      return;
    }
    this._workflowService
      .getTrackingDetailsByTrackingNumber(
        trackingNumber,
        this.trackingLineRecIdD365
      )
      .subscribe({
        next: res => {
          if (res?.data) {
            this.trackingNumberDetail = res.data;
            if (
              res?.data.deliveryStatus?.toLowerCase() !== "pending" &&
              res?.data.deliveryStatus !== ""
            ) {
              if (res?.data?.weight && res?.data?.weight !== "") {
                this.trackingWeight = JSON.parse(
                  res?.data?.weight ?? ""
                ) as WeightDto[];
              }
              if (res?.data?.weight && res?.data?.dimension !== "") {
                this.trackingDimension = JSON.parse(
                  res?.data?.dimension ?? ""
                ) as DimensionDto[];
              }
            }
            this.mapTrackingNumberDetailData();
            this.isUPS =
              !!this.trackingNumberDetail.shippingMethodD365?.match("UP");
            this.trackingDetailsModalOpen.set(true);
          } else {
            this._toastr.error("Error", "No Tracking details Found");
          }
        },
        error: res => {
          this._toastr.error("Error", res.message);
        },
      });
  }

  private mapTrackingNumberDetailData(): void {
    this.trackingDetailsBindingObj = {
      workFlowId: this.trackingNumberDetail.workFlowId ?? 0,
      actualDateTime: this.trackingNumberDetail.actualDateTime ?? "",
      address: this.trackingNumberDetail.address ?? "",
      shipperInformation:
        this.getStringWithComma(this.trackingNumberDetail.shipperCity ?? "") +
        this.getStringWithComma(
          this.trackingNumberDetail.shipperProvinceCode ?? ""
        ) +
        this.getStringWithComma(
          this.trackingNumberDetail.shipperCountryName ?? "",
          false
        ),
      recipientInformation:
        this.getStringWithComma(this.trackingNumberDetail.recipientCity ?? "") +
        this.getStringWithComma(
          this.trackingNumberDetail.recipientProvinceCode ?? ""
        ) +
        this.getStringWithComma(
          this.trackingNumberDetail.recipientCountryName ?? "",
          false
        ),
      vendorSkuD365: this.trackingNumberDetail.vendorSkuD365 ?? "",
      status: this.trackingNumberDetail.status ?? "",
      quantityD365: this.trackingNumberDetail.quantityD365 ?? 0,
      deliveryStatus: this.trackingNumberDetail.deliveryStatus ?? "",
      description: this.trackingNumberDetail.description ?? "",
      shippingMethodD365: this.trackingNumberDetail.shippingMethodD365,
      shippingMethod: this.trackingNumberDetail.shippingMethod,
      trackingNumber: this.trackingNumberDetail.trackingNumber,
    };

    const weightString = [] as string[];
    const dimesionString = [] as string[];
    this.trackingWeight.forEach(element => {
      if (this.isUPS) {
        weightString.push(
          (element.Weight ?? "") + " " + element.UnitOfMeasurement
        );
      } else {
        weightString.push((element.Value ?? "") + " " + element.Unit);
      }
    });
    this.trackingDimension.forEach(element => {
      const unitOfDimension = element.UnitOfDimension ?? "";
      const units = element.Units ?? "";
      if (this.isUPS) {
        dimesionString.push(
          "Length: " +
            (element.Length ?? 0) +
            " " +
            unitOfDimension +
            ", Width: " +
            (element.Width ?? 0) +
            " " +
            unitOfDimension +
            ", Height: " +
            (element.Height ?? 0) +
            " " +
            unitOfDimension
        );
      } else {
        dimesionString.push(
          "Length: " +
            (element.Length ?? 0) +
            " " +
            units +
            ", Width: " +
            (element.Width ?? 0) +
            " " +
            units +
            ", Height: " +
            (element.Height ?? 0) +
            " " +
            units
        );
      }
    });
    this.trackingDetailsBindingObj.weight = weightString;
    this.trackingDetailsBindingObj.dimension = dimesionString;
  }

  private getStringWithComma(
    value: string | null,
    isCommaApplied = true
  ): string {
    return value && value !== "" ? value + (isCommaApplied ? ", " : "") : "";
  }

  private getManualShippingMethods(): void {
    this._workflowService.getManualTrackingShippingMethods().subscribe({
      next: res => {
        this.manualShippingMethods = res?.data ?? [];
        this.manualMethod = "";
        this.trackings = "";
        this.displayAddManualTrackingModal.set(true);
      },
    });
  }

  openAddManualTrackingModal(): void {
    this.manualMethod = "";
    this.trackings = "";
    this.getManualShippingMethods();
  }

  addManualTrackingNumbers(): void {
    if (this.trackings === "") {
      this._toastr.error("Error", "Enter Tracking Numbers");
      return;
    }
    if (this.manualMethod === "" || !this.manualMethod) {
      this._toastr.error("Error", "Select a Shipping Method");
      return;
    }

    const trackingNumbers = this.trackings.split(",");
    const trackingData = trackingNumbers.map((obj: string) => {
      return {
        Quantity: 0,
        VendorSKU: this.trackingLineVendorSKU,
        LineNumber: 0,
        TrackingNumber: String(obj).trim(),
        PurchaseOrder: this.addTrackingPurchaseOrder,
        IsMasterTracking: false,
        ShippingMethod: this.manualMethod,
      };
    });
    this.addTrackingData(trackingData, true);
  }

  updateManualTrackingStatus(event: ClientSideLineTrackingListDto): void {
    if (!event) {
      this._loaderService.setLoaderState(false);
      this._toastr.error("Error", "Invalid Request");
      return;
    }

    const request = {
      trackingLineId: event.id,
      trackingNumber: event.trackingNumber,
      status: event.deliveryStatusId ?? 0,
      lineRecId: event.lineRecIdD365,
      workflowId: this.workflowId(),
      vendorSKU: event.vendorSKU ?? "",
    } as UpdateManualTrackingStatusDto;

    this._workflowService.updateManualTrackingStatus(request).subscribe({
      next: res => {
        this._loaderService.setLoaderState(false);
        if (res?.data) {
          this._toastr.success(
            "Success",
            "Tracking Status updated Successfully"
          );
          this.loadSamples(this.workflowId());
          this.getSampleTrackingList();
        } else {
          this._toastr.error("Error", "Failed to update Tracking Status");
        }
      },
      error: () => {
        this._loaderService.setLoaderState(false);
        this._toastr.error("Error", "Failed to update Tracking Status");
      },
    });
  }

  closeModal(): void {
    this.addTrackingModal.set(false);
    this.trackingListModal.set(false);
  }

  closeModalDialog(): void {
    this.trackingListModal.set(false);
    this.addTrackingModal.set(false);
    this.displayAddManualTrackingModal.set(false);
  }

  closeTrackingModals(): void {
    this.trackingHistoryModalOpen.set(false);
    this.trackingDetailsModalOpen.set(false);
  }

  // ─── Notes ─────────────────────────────────────────────────────────

  /** Opens the notes sidebar for a sample line (add or edit). */
  showInputFieldForLineNote(sample: WorkflowSampleLineDto): void {
    this.notes = sample.notes ?? "";
    this.noteLineRecId = sample.lineRecIdD365 ?? 0;
    this.displaySidebarForNotes = true;
  }

  closeSideBarForNotes(): void {
    this.displaySidebarForNotes = false;
  }

  /** Saves the sample line note and refreshes the list. */
  saveNotes(request: AddProjectWorkflowLineNotesDto): void {
    this._loaderService.setLoaderState(true);
    const payload: AddProjectWorkflowLineNotesDto = {
      projectWorkflowId: this.workflowId(),
      lineRecId: request.lineRecId,
      notes: request.notes,
    };
    this._workflowService.addSampleLineNotes(payload).subscribe({
      next: res => {
        this._loaderService.setLoaderState(false);
        if (res.status?.toLowerCase() === "success") {
          this._toastr.success(
            "Success",
            res.message || "Note saved successfully"
          );
          this.displaySidebarForNotes = false;
          this.loadSamples(this.workflowId());
        } else {
          this._toastr.error("Error", res.message || "Error updating note");
        }
      },
      error: res => {
        this._loaderService.setLoaderState(false);
        this._toastr.error("Error", res.message || "Error updating note");
      },
    });
  }
}
